import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../../components/dashboard-layout";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  FileSpreadsheet,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Download,
  Check,
  X,
  Building2,
  Globe,
  
} from "lucide-react";
import { useAuth } from "../../../contexts/auth-context";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../../../contexts/theme-provider";
import {
  downloadMemoOutputFile,
  handleGetBanksHasReportByUser,
} from "../../../services/reports";
import type { PeerGroupReport } from "../../../services/reports";
import {
  getAllCountries,
} from "../../../services/lookup";
import { Label } from "@radix-ui/react-label";
import { SearchableSelect } from "../../../components/searchable-select";
import { cn } from "../../../lib/utils";

export default function PeerGroupAnalysisPage() {
  const { theme } = useTheme();
  const [filteredReports, setFilteredReports] = useState<PeerGroupReport[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedReports, setSelectedReports] = useState<Set<number>>(
    new Set(),
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [clientsFilter, setclientsFilter] = useState<string>("all");
  const [countryFilter, setCountryFilter] = useState<string>("all");
  const [bankFilter, setBankFilter] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [availableCountries, setAvailableCountries] = useState<any>([]);
  const [token, setToken] = useState<string>();
  const [outputMode, setOutputMode] = useState<"generic" | "own">("generic");
  const [isLoadingCountries] = useState(false);
  const [countriesError] = useState<string | null>(null);
  
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || !token) return;
    setMounted(true);

    if (countryFilter !== "all") {
      fetchReports();
    } else {
      setFilteredReports([]);
      setTotalCount(0);
      setSelectedReports(new Set());
      setIsLoading(false);
    }
  }, [user, token, countryFilter, outputMode]);

  useEffect(() => {
    if (!user?.access_token) return;

    setToken(user.access_token);
  }, [user?.access_token]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    statusFilter,
    clientsFilter,
    countryFilter,
    bankFilter,
    startDate,
    endDate,
  ]);

  const fetchReports = async () => {
    if (!user?.id || !token) {
      console.warn("No user available, skipping fetchReports");
      return;
    }
    try {
      setIsLoading(true);
      const reportsData = await handleGetBanksHasReportByUser(
        user.id,
        countryFilter,
        token,
        outputMode === "own",
        currentPage,
        itemsPerPage
      );

      setFilteredReports(reportsData.data);
      setTotalCount(reportsData.total);
      setSelectedReports(new Set());
    } catch (error) {
      console.error("Error fetching reports:", error);
      setFilteredReports([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;

    const fetchCountries = async () => {
      try {
        const countries = await getAllCountries(token);
        setAvailableCountries(countries);
      } catch (error) {
        console.error("Failed to fetch countries:", error);
      }
    };

    fetchCountries();
  }, [token]); // re-run if token changes

  useEffect(() => {
    if (user?.id && token && countryFilter !== "all") {
      fetchReports();
    } else {
      setIsLoading(false);
    }
  }, [currentPage, countryFilter, outputMode, token]); // refetch when page or filters change

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleSelectReport = (reportId: number) => {
    const newSelected = new Set(selectedReports);
    if (newSelected.has(reportId)) {
      newSelected.delete(reportId);
    } else {
      newSelected.add(reportId);
    }
    setSelectedReports(newSelected);
  };

  const handleSelectAllOnPage = () => {
    const currentPageIds = new Set(filteredReports.map((r) => r.id));
    const newSelected = new Set(selectedReports);

    const allSelected = filteredReports.every((r) => newSelected.has(r.id));

    if (allSelected) {
      currentPageIds.forEach((id) => newSelected.delete(id));
    } else {
      currentPageIds.forEach((id) => newSelected.add(id));
    }

    setSelectedReports(newSelected);
  };

  const handleSelectAllFiltered = () => {
    const filteredIds = new Set(filteredReports.map((r) => r.id));
    const newSelected = new Set(selectedReports);

    const allSelected = filteredReports.every((r) => newSelected.has(r.id));

    if (allSelected) {
      filteredIds.forEach((id) => newSelected.delete(id));
    } else {
      filteredIds.forEach((id) => newSelected.add(id));
    }

    setSelectedReports(newSelected);
  };

  const handleDownloadSelected = async () => {
    if (selectedReports.size === 0 || !token) return;
    if (!user?.id) return;

    try {
      setIsDownloading(true);

      const reportsToDownload = filteredReports.filter((r) =>
        selectedReports.has(r.id),
      );

      if (reportsToDownload.length === 0) {
        alert("Please select at least one organization.");
        setIsDownloading(false);
        return;
      }

      // Derive country from selected reports if not set
      let downloadCountry = countryFilter;
      if (!downloadCountry || countryFilter === "all") {
        if (reportsToDownload.length > 0) {
          // Check if all selected reports have the same country
          const countries = [...new Set(reportsToDownload.map((r) => r.country))];
          if (countries.length > 1) {
            alert("Selected organizations are from different countries. Please select organizations from the same country or select a country from the dropdown.");
            setIsDownloading(false);
            return;
          }
          downloadCountry = reportsToDownload[0].country;
        }
      }

      const downloadResults: Array<{ success: boolean; error?: string }> = [];

      try {
        const resp = await downloadMemoOutputFile(
          Array.from(selectedReports),
          user?.id,
          downloadCountry,
          token,
          outputMode === "own",
        );
        console.log(resp)
        downloadResults.push({ success: true });
      } catch (error) {
        downloadResults.push({
          success: false,
          error: error instanceof Error ? error.message : "Unknown error",
        });
      }

      const successCount = downloadResults.filter((r) => r.success).length;
      const errorCount = downloadResults.filter((r) => !r.success).length;

      if (errorCount === 0) {
        console.log(`✅ Successfully downloaded all ${successCount} report(s)`);
      } else {
        const errorMessage = downloadResults.find((r) => !r.success)?.error;
        alert(`⚠️ Download failed:\n${errorMessage || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error in download operation:", error);
      alert("An unexpected error occurred. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedReports(new Set());
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setclientsFilter("all");
    setCountryFilter("all");
    setBankFilter("all");
    setStartDate("");
    setEndDate("");
  };

  const activeFiltersCount = [
    searchTerm !== "",
    statusFilter !== "all",
    clientsFilter !== "all",
    countryFilter !== "all",
    bankFilter !== "all",
    startDate !== "",
    endDate !== "",
  ].filter(Boolean).length;

  if (!mounted) return null;

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="p-4 md:p-8 text-center min-h-screen flex items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{
              duration: 2,
              repeat: Number.POSITIVE_INFINITY,
              ease: "linear",
            }}
            className="h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full"
          />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div
        className={`p-4 md:p-8 min-h-screen ${theme === "dark" ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" : "bg-gradient-to-br from-slate-50 via-white to-blue-50"}`}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-6"
        >
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          >
            <div className="w-full sm:w-auto">
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={`text-2xl md:text-3xl font-black bg-clip-text text-transparent ${
                  theme === "dark"
                    ? "bg-gradient-to-r from-white via-blue-100 to-purple-100"
                    : "bg-gradient-to-r from-slate-900 via-blue-900 to-purple-900"
                }`}
              >
                Country Memo
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={`font-medium mt-1 text-sm ${theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}`}
              >
              </motion.p>
            </div>
            {/* <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }} className="w-full sm:w-auto">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="flex justify-end sm:justify-start">
                <Button 
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg shadow-blue-500/25 rounded-xl w-full sm:w-auto text-sm h-9"
                  onClick={() => navigate("/user/dashboard")}
                >
                  <Plus className="h-3.5 w-3.5 mr-2" />
                  Create New Report
                </Button>
              </motion.div>
            </motion.div> */}
          </motion.div>

          {/* Reports Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <Card
              className={`backdrop-blur-xl shadow-lg md:shadow-xl ${theme === "dark" ? "bg-slate-800/70 shadow-black/20 border-slate-700/50" : "bg-white/70 shadow-black/5 border-slate-200/50"}`}
            >
              <CardHeader className="p-4 md:p-6 pb-3">
                <div className="flex flex-col gap-4">
                  {/* Title and Batch Controls Row */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <motion.div
                        animate={{ rotate: [0, 360] }}
                        transition={{
                          duration: 4,
                          repeat: Number.POSITIVE_INFINITY,
                          ease: "linear",
                        }}
                        className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-md flex items-center justify-center"
                      >
                        <Sparkles className="h-3 w-3 text-white" />
                      </motion.div>
                      <CardTitle
                        className={`text-lg md:text-xl font-black ${theme === "dark" ? "text-white" : "text-slate-900 dark:text-white"}`}
                      >
                        Banks ({totalCount})
                      </CardTitle>
                    </div>

                    {selectedReports.size > 0 && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex items-center gap-2"
                      >
                        <Badge variant="secondary" className="text-xs">
                          {selectedReports.size} selected
                        </Badge>

                        <Button
                          size="sm"
                          onClick={handleDownloadSelected}
                          className="h-8 text-white text-xs bg-green-600 hover:bg-green-700"
                          disabled={
                            selectedReports.size === 0 || isDownloading
                          }
                        >
                          {isDownloading ? (
                            <div className="h-3.5 w-3.5 mr-1.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Download className="h-3.5 w-3.5 mr-1.5" />
                          )}
                          {isDownloading
                            ? "Downloading..."
                            : "Generate Country Memo"}
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleClearSelection}
                          className="h-8 text-xs"
                        >
                          <X className="h-3.5 w-3.5 mr-1.5" />
                          Clear
                        </Button>
                      </motion.div>
                    )}
                  </div>

                  {/* Search and Filters Row */}
                  <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-2">
                    <div className="flex flex-wrap items-center gap-6">
                      {/* Country Filter */}
                      {availableCountries.length > 0 && (
                        <div
                          className="space-y-2 relative"
                          style={{ zIndex: 50 }}
                        >
                          <Label
                            className={cn(
                              "flex items-center gap-2 text-sm font-semibold",
                              theme === "dark"
                                ? "text-gray-300"
                                : "text-gray-700 dark:text-slate-200",
                            )}
                          >
                            <Globe className="w-4 h-4" />
                            Country <span className="text-red-500 dark:text-red-400">*</span>
                          </Label>
                          <div className="relative" style={{ zIndex: 50 }}>
                            <SearchableSelect
                              options={availableCountries.map(
                                (c: { iso2: string; name: string }) => ({
                                  value: c.iso2,
                                  label: c.name,
                                }),
                              )}
                              value={countryFilter}
                              onChange={setCountryFilter}
                              placeholder="Type or select a country" // Changed from "Select or type a country"
                              disabled={isLoadingCountries}
                              theme={theme}
                              inputStyleProp="w-full overflow-y-visible p-1 px-2 rounded-md border-2 text-base text-sm transition-all shadow-md"
                              listStyleProp="p-2 text-sm cursor-pointer transition-colors"
                              clearable={false}
                            />
                          </div>
                          {countriesError && (
                            <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                              {countriesError}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Output Mode Filter */}
                      <div className="space-y-2 relative" style={{ zIndex: 50 }}>
                        <Label
                          className={cn(
                            "flex items-center gap-2 text-sm font-semibold",
                            theme === "dark" ? "text-gray-300" : "text-gray-700 dark:text-slate-200",
                          )}
                        >
                          Reports Generated By
                        </Label>
                        <div className="relative" style={{ zIndex: 50 }}>
                          <select
                            value={outputMode}
                            onChange={(e) => setOutputMode(e.target.value as "generic" | "own")}
                            className={cn(
                              "w-full p-1 px-2 rounded-md border-2 text-sm transition-all shadow-sm focus:ring-2 focus:ring-blue-500/50",
                              theme === "dark"
                                ? "bg-gray-700/70 border-gray-600 text-white focus:border-blue-500"
                                : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white focus:border-blue-500",
                            )}
                          >
                            <option value="generic">All</option>
                            <option value="own">My</option>
                          </select>
                        </div>
                      </div>

                      {activeFiltersCount > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleClearFilters}
                          className="h-15 text-xs pt-5"
                        >
                          <X className="h-3.5 w-3.5 mr-1.5" />
                          Clear ({activeFiltersCount})
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-0">
                <AnimatePresence mode="wait">
                  {filteredReports.length === 0 ? (
                    <motion.div
                      key="empty"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.3 }}
                      className="text-center py-12 px-4"
                    >
                      <FileSpreadsheet
                        className={`h-12 w-12 mx-auto mb-3 ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`}
                      />
                      <p
                        className={`text-base font-semibold ${theme === "dark" ? "text-slate-400" : "text-slate-500 dark:text-slate-400"}`}
                      >
                        {countryFilter === "all"
                          ? "Select a country"
                          : "No reports found"}
                      </p>
                      <p
                        className={`text-sm mt-1 ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`}
                      >
                        {countryFilter === "all"
                          ? "Choose a country from the dropdown to view organizations"
                          : searchTerm ||
                            statusFilter !== "all" ||
                            clientsFilter !== "all" ||
                            bankFilter !== "all" ||
                            startDate ||
                            endDate
                          ? "Try adjusting your search or filters"
                          : "Create your first report to get started"}
                      </p>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="table"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-x-auto"
                    >
                      <Table className="min-w-[1000px] md:min-w-full">
                        <TableHeader
                          className={
                            theme === "dark"
                              ? "bg-slate-700/30"
                              : "bg-slate-50/50 dark:bg-slate-900/50"
                          }
                        >
                          <TableRow
                            className={`${theme === "dark" ? "border-slate-700" : "border-slate-200"}`}
                          >
                            <TableHead
                              className={`font-bold text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"} py-3 w-10`}
                            >
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={
                                    filteredReports.length > 0 &&
                                    filteredReports.every((r) =>
                                      selectedReports.has(r.id),
                                    )
                                  }
                                  onChange={handleSelectAllOnPage}
                                  className={`h-4 w-4 rounded ${
                                    theme === "dark"
                                      ? "bg-slate-700 border-slate-600 text-blue-500 focus:ring-blue-600"
                                      : "bg-white border-slate-300 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
                                  }`}
                                  title="Select all on page"
                                />
                              </div>
                            </TableHead>
                            <TableHead
                              className={`font-bold text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"} py-3`}
                            >
                              <div className="flex items-center gap-1">
                                <Building2 className="h-3.5 w-3.5" />
                                Organization
                              </div>
                            </TableHead>
                            <TableHead
                              className={`font-bold text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"} py-3`}
                            >
                              <div className="flex items-center gap-1">
                                <Globe className="h-3.5 w-3.5" />
                                Country
                              </div>
                            </TableHead>
                            {/* <TableHead className={`font-bold text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"} py-3`}>Status</TableHead>
                            <TableHead className={`font-bold text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"} py-3`}>Created</TableHead> */}
                          </TableRow>
                        </TableHeader>

                        <TableBody>
                          {filteredReports.map((report: any, index: number) => (
                            <motion.tr
                              key={report.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                delay: index * 0.05,
                                duration: 0.3,
                              }}
                              className={`transition-all duration-200 border-slate-200 ${theme === "dark" ? "dark:border-slate-700" : ""} ${
                                selectedReports.has(report.id)
                                  ? theme === "dark"
                                    ? "bg-blue-900/20"
                                    : "bg-blue-50"
                                  : theme === "dark"
                                    ? "dark:hover:bg-slate-700/30"
                                    : "hover:bg-slate-50/50"
                              }`}
                            >
                              <TableCell className="py-3">
                                <input
                                  type="checkbox"
                                  checked={selectedReports.has(report.id)}
                                  onChange={() => handleSelectReport(report.id)}
                                  className={`h-4 w-4 rounded ${
                                    theme === "dark"
                                      ? "bg-slate-700 border-slate-600 text-blue-500 focus:ring-blue-600"
                                      : "bg-white border-slate-300 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
                                  }`}
                                />
                              </TableCell>

                              <TableCell
                                className={`font-medium text-xs py-3 ${
                                  report.bank_name === "Unknown"
                                    ? theme === "dark"
                                      ? "text-slate-500 italic"
                                      : "text-slate-400 italic"
                                    : theme === "dark"
                                      ? "text-slate-300"
                                      : "text-slate-700"
                                }`}
                              >
                                <div className="flex items-center gap-1.5">
                                  <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                                  <span
                                    className="truncate max-w-[150px]"
                                    title={report.bank_name}
                                  >
                                    {report.bank_name}
                                  </span>
                                </div>
                              </TableCell>

                              <TableCell
                                className={`font-medium text-xs py-3 cursor-pointer ${
                                  report.country === "Unknown"
                                    ? theme === "dark"
                                      ? "text-slate-500 italic"
                                      : "text-slate-400 italic"
                                    : theme === "dark"
                                      ? "text-slate-300"
                                      : "text-slate-700"
                                }`}
                                onClick={() =>
                                  navigate(`/user/reports/${report.id}`)
                                }
                              >
                                <div className="flex items-center gap-1.5">
                                  <Globe className="h-3.5 w-3.5 flex-shrink-0" />
                                  <span
                                    className="truncate max-w-[120px]"
                                    title={report.country}
                                  >
                                    {report.country}
                                  </span>
                                </div>
                              </TableCell>

                              {/* <TableCell className="py-3">{getStatusBadge(report.status)}</TableCell> */}

                              {/* <TableCell 
                                className={`font-medium text-xs ${theme === "dark" ? "text-slate-400" : "text-slate-600 dark:text-slate-400"} py-3 cursor-pointer`}
                                onClick={() => navigate(`/user/reports/${report.id}`)}
                              >
                                {new Date(report.timestamp).toLocaleDateString()}
                              </TableCell> */}
                            </motion.tr>
                          ))}
                        </TableBody>
                      </Table>

                      {/* Selection Summary and Pagination */}
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 md:px-6 py-3 border-t border-slate-200 dark:border-slate-700 gap-3"
                      >
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                          <div
                            className={`text-xs font-medium ${theme === "dark" ? "text-slate-400" : "text-slate-600 dark:text-slate-400"}`}
                          >
                            {selectedReports.size > 0 ? (
                              <div className="flex items-center gap-2">
                                <span>
                                  {selectedReports.size} report(s) selected
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={handleSelectAllFiltered}
                                  className="h-6 text-xs"
                                >
                                  {filteredReports.every((r) =>
                                    selectedReports.has(r.id),
                                  )
                                    ? "Deselect all"
                                    : "Select all filtered"}
                                </Button>
                                {filteredReports.every((r) =>
                                  selectedReports.has(r.id),
                                ) && (
                                  <Badge variant="outline" className="text-xs">
                                    <Check className="h-3 w-3 mr-1" />
                                    All filtered
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span>
                                Showing{" "}
                                {Math.min(
                                  currentPage * itemsPerPage,
                                  totalCount,
                                )}{" "}
                                of {totalCount} reports
                              </span>
                            )}
                          </div>
                        </div>

                        {totalPages > 1 && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handlePreviousPage}
                              disabled={currentPage === 1}
                              className={`h-7 cursor-pointer text-xs ${theme === "dark" ? "border-slate-600" : ""}`}
                            >
                              <ChevronLeft className="h-3 w-3 mr-1" />
                              Previous
                            </Button>
                            <div
                              className={`text-xs font-medium px-2 ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"}`}
                            >
                              {currentPage} / {totalPages}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleNextPage}
                              disabled={currentPage === totalPages}
                              className={`h-7 cursor-pointer text-xs ${theme === "dark" ? "border-slate-600" : ""}`}
                            >
                              Next
                              <ChevronRight className="h-3 w-3 ml-1" />
                            </Button>
                          </div>
                        )}
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
